import { Eye, Sparkles, CalendarDays } from "lucide-react";
import { useReveal } from "@/hooks/useReveal";

const SERVICES = [
  {
    key: "viewing",
    icon: Eye,
    title: "Request Private Viewing",
    text: "An in-person encounter with the maison's pieces — held in Monaco, Geneva, or by arrangement.",
    cta: "Arrange Viewing",
  },
  {
    key: "pricing",
    icon: Sparkles,
    title: "Request Pricing",
    text: "Discreet pricing is shared only after introduction. The maison does not publish figures.",
    cta: "Request Pricing",
  },
  {
    key: "appointment",
    icon: CalendarDays,
    title: "Private Appointment",
    text: "A confidential consultation with a maison concierge — for acquisitions, commissions, and counsel.",
    cta: "Reserve Appointment",
  },
];

export default function PrivateAcquisitionsSection({ onSelect }) {
  const eyebrow = useReveal();
  const title = useReveal();
  const lead = useReveal();

  return (
    <section
      id="acquisitions"
      data-testid="acquisitions-section"
      className="relative bg-black py-28 md:py-40 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <p ref={eyebrow} className="pd-reveal eyebrow" data-testid="acq-eyebrow">A Concierge Maison</p>
          <h2
            ref={title}
            className="pd-reveal mt-6 font-serif-display text-4xl md:text-6xl lg:text-7xl text-white tracking-[0.06em] leading-[1.05]"
            style={{ transitionDelay: "160ms" }}
            data-testid="acq-title"
          >
            Private Acquisitions.
          </h2>
          <div ref={lead} className="pd-reveal mt-8" style={{ transitionDelay: "320ms" }}>
            <p className="font-light text-white/70 text-base md:text-lg leading-relaxed tracking-wide">
              Pieces are not sold — they are entrusted. Begin a conversation, and the
              maison will reply in kind.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--pd-border)]">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.key} service={s} index={i} onSelect={onSelect} />
          ))}
        </div>

        <p className="text-center text-white/45 text-xs uppercase tracking-[0.4em] mt-16">
          No carts. No checkout. Private access only.
        </p>
      </div>
    </section>
  );
}

function ServiceCard({ service, index, onSelect }) {
  const r = useReveal();
  const Icon = service.icon;
  return (
    <div
      ref={r}
      data-testid={`acq-card-${service.key}`}
      style={{ transitionDelay: `${index * 140}ms` }}
      className="pd-reveal group relative bg-black hover:bg-[#070605] transition-colors duration-700 p-10 md:p-12 flex flex-col"
    >
      <Icon className="text-[var(--pd-gold)] mb-8" size={28} strokeWidth={1} />
      <h3 className="font-serif-display text-2xl md:text-3xl text-white tracking-[0.06em]">{service.title}</h3>
      <p className="mt-5 text-white/60 text-sm leading-relaxed">{service.text}</p>
      <div className="mt-auto pt-10">
        <button
          data-testid={`acq-btn-${service.key}`}
          onClick={() => onSelect?.(service.key)}
          className="text-[0.7rem] uppercase tracking-[0.36em] text-[var(--pd-gold)] border-b border-[var(--pd-gold)] pb-1 hover:tracking-[0.46em] transition-all duration-500"
        >
          {service.cta} →
        </button>
      </div>
    </div>
  );
}
